<script setup>
import { useStore } from "vuex";
import { useLoading } from "vue-loading-overlay";
import { computed, onMounted, ref } from "vue";
import moment from "moment";
import DataTable from "@/components/panels/DataTable.vue";
import RowActions from "@/components/panels/RowActions.vue";
import PaginationBar from "@/components/layouts/PaginationBar.vue";

const store = useStore();
const $loading = useLoading({});

const logList = computed(() => store.getters['log/logList']);
const organizationList = computed(() => store.getters['organization/organizationList']);
const lockerList = computed(() => store.getters['locker/lockerList']);
const userList = computed(() => store.getters['user/userList']);
const currentUserData = computed(() => store.getters['user/currentUserData']);
const logs = computed(() => logList.value?.results || []);
const isAdmin = computed(() => currentUserData.value.role === 'administrator');

const page = ref(1);
const organization = ref('');
const device = ref('');
const employee = ref('');
const logType = ref('');

const LOG_TYPES = [
  { filter: 'status_changed', name: 'Status changed' },
  { filter: 'key_changed', name: 'Key changed' },
  { filter: 'organization_changed', name: 'Organization changed' },
  { filter: 'address_changed', name: 'Address changed' },
  { filter: 'name_changed', name: 'Name changed' },
  { filter: 'model_changed', name: 'Model changed' },
  { filter: 'control_unit_changed', name: 'Control unit changed' },
  { filter: 'cell_board_changed', name: 'Cell board replaced' },
  { filter: 'cell_is_broken_changed', name: 'Cell is_broken changed' },
  { filter: 'cell_is_active_changed', name: 'Cell is_active changed' },
  { filter: 'cell_cleaning_changed', name: 'Cell cleaning changed' },
  { filter: 'cell_firmware_changed', name: 'Cell firmware changed' },
  { filter: 'head_firmware_changed', name: 'Head firmware changed' },
];

function getFilteredLogList() {
  const loader = $loading.show({});
  let filter = `?page=${page.value}`;
  if (organization.value) filter += `&organization=${organization.value}`;
  if (device.value) filter += `&device=${device.value}`;
  if (logType.value) filter += `&type=${logType.value}`;
  if (employee.value) filter += `&user=${employee.value}`;
  return store.dispatch('log/getLogList', filter)
    .finally(() => loader.hide());
}

function applyFilters() {
  page.value = 1;
  getFilteredLogList();
}

onMounted(() => {
  getFilteredLogList();
  store.dispatch('locker/getLockerList', '?page_size=1000');
  store.dispatch('organization/getOrganizationList', '?page_size=1000');
  store.dispatch('user/getUserList', '?page_size=1000');
});

function iconOf(log) {
  switch (log.type) {
    case 'status_changed':
      return log.extra?.new_status === 'online' ? 'access-point' : 'access-point-off';
    case 'key_changed': return 'key-outline';
    case 'organization_changed': return 'office-building-outline';
    case 'address_changed': return 'map-marker-outline';
    case 'name_changed': return 'rename-box';
    case 'model_changed': return 'shape-outline';
    case 'control_unit_changed': return 'memory';
    case 'cell_board_changed': return 'chip';
    case 'cell_is_broken_changed': return 'link-variant-off';
    case 'cell_is_active_changed': return 'shield-check-outline';
    case 'cell_cleaning_changed': return 'broom';
    case 'wifi_changed': return 'wifi';
    case 'head_firmware_changed':
    case 'cell_firmware_changed': return 'cloud-check-outline';
    default: return 'cog-outline';
  }
}

// types the backend sends but LOG_TYPES doesn't map yet (e.g. device_boot_event)
// arrive as raw snake_case — show them humanised instead of as a raw token
function humanizeType(type) {
  const words = String(type ?? '').replace(/_/g, ' ').trim();
  return words ? words[0].toUpperCase() + words.slice(1) : '—';
}

const typeName = (type) => LOG_TYPES.find((t) => t.filter === type)?.name || humanizeType(type);

function detailOf(log) {
  if (log.type === 'status_changed') {
    return `${log.extra?.old_status || '?'} → ${log.extra?.new_status || '?'}`;
  }
  // boards are only swapped through replace-board now, so every entry reads as a replacement
  if (log.type === 'cell_board_changed') {
    return `Board replaced in cell #${log.extra?.cell_number ?? '?'}, old address ${log.extra?.old_cell_board ?? '?'}`;
  }
  if (log.extra) {
    const extras = Object.entries(log.extra)
      .filter(([, v]) => v !== null && v !== '')
      .map(([k, v]) => `${k.replace(/_/g, ' ')}: ${v}`);
    if (extras.length) return extras.join(' · ');
  }
  return '—';
}

const fmt = (ts) => (ts ? moment(ts).format('MMM D, HH:mm') : '—');

// Action type is 210px, not the 180px of the reference design: the demo data there
// used short labels ("Sync", "Open cell"), while the real ones run up to
// "Head firmware changed" (200px measured) and wrapped to two lines at 180px.
const cols = 'minmax(130px,1fr) 150px 210px minmax(240px,1.8fr) 150px 150px';
const columns = ['Device', 'Employee', 'Action type', 'Detail', 'Created at', 'Organization'];
</script>

<template>
  <!-- toolbar: 4 filters -->
  <div class="toolbar">
    <div v-if="lockerList" class="field-select">
      <span class="mdi mdi-locker lead"></span>
      <select v-model="device" @change="applyFilters">
        <option value="">All devices</option>
        <option v-for="d in lockerList.results || []" :key="d.sn" :value="d.sn">{{ d.name }}</option>
      </select>
      <span class="mdi mdi-chevron-down caret"></span>
    </div>
    <div v-if="userList" class="field-select">
      <span class="mdi mdi-account-outline lead"></span>
      <select v-model="employee" @change="applyFilters">
        <option value="">All employees</option>
        <option v-for="u in userList.results || []" :key="u.id" :value="u.id">{{ u.first_name || u.email }}</option>
      </select>
      <span class="mdi mdi-chevron-down caret"></span>
    </div>
    <div class="field-select">
      <span class="mdi mdi-flash-outline lead"></span>
      <select v-model="logType" @change="applyFilters">
        <option value="">All actions</option>
        <option v-for="t in LOG_TYPES" :key="t.filter" :value="t.filter">{{ t.name }}</option>
      </select>
      <span class="mdi mdi-chevron-down caret"></span>
    </div>
    <div v-if="isAdmin && organizationList" class="field-select">
      <span class="mdi mdi-office-building-outline lead"></span>
      <select v-model="organization" @change="applyFilters">
        <option value="">All organizations</option>
        <option v-for="o in organizationList.results || []" :key="o.id" :value="o.id">{{ o.name }}</option>
      </select>
      <span class="mdi mdi-chevron-down caret"></span>
    </div>
    <div class="spacer"></div>
    <span class="count">{{ logList?.count ?? 0 }} events</span>
  </div>

  <!-- table -->
  <DataTable :cols="cols" :columns="columns" :rows="logs" row-key="created_at" empty-text="No log events match your filters.">
    <template #row="{ row }">
      <div class="td">
        <div class="td-name">
          <div class="ic"><span class="mdi mdi-locker"></span></div>
          <span class="strong">{{ row.device }}</span>
        </div>
      </div>
      <div class="td">
        <span v-if="row.employee">{{ row.employee }}</span>
        <span v-else class="muted">System</span>
      </div>
      <div class="td">
        <span class="chip">
          <span class="mdi" :class="`mdi-${iconOf(row)}`"></span>
          {{ typeName(row.type) }}
        </span>
      </div>
      <div class="td"><span class="muted">{{ detailOf(row) }}</span></div>
      <div class="td"><span class="muted">{{ fmt(row.created_at) }}</span></div>
      <div class="td"><span>{{ row.organization || '—' }}</span></div>
      <RowActions
        :copies="[
          { icon: 'content-copy', label: 'Copy detail', text: detailOf(row) },
          { icon: 'devices', label: 'Copy device', text: row.device || '' },
        ]" />
    </template>
  </DataTable>

  <PaginationBar
    :page="page"
    :total-pages="logList?.total_pages || 1"
    :count="logList?.count || 0"
    @update:page="(p) => { page = p; getFilteredLogList(); }" />
</template>
